import { isMobile } from 'react-device-detect'

import Collection from '@src/pages/[...slug]/components/collection/domains/Collection'
import { IProductFeedResponseForCollection } from '@src/pages/[...slug]/components/collection/services/collectionService/CollectionService.types'

import MemoryBoundedLRUCache from '../../utils/memoryBoundedLRUCache'

import type { Pagination } from '@lastbrand/storefront-proto-registry/api/v1/common'
import type { Widget } from '@lastbrand/storefront-proto-registry/api/v1/layoutfetch'
import type { WidgetFetchResponse } from '@lastbrand/storefront-proto-registry/api/v1/widgetfetch'

/**
 * Widget pagination state for a single widget
 */
export interface WidgetPaginationState {
  widgetId: string
  dataItems: unknown[] // Accumulated items from pagination
  paginationInfo: Pagination | undefined // Current cursor and hasMore
  paginatedPages: WidgetFetchResponse[] // Pages of data loaded via pagination
}

/**
 * Layout pagination state for the entire page
 * Stores layout-level pagination data (cursor, accumulated widgets)
 */
export interface LayoutPaginationState {
  paginationInfo: Pagination | undefined // Layout-level cursor and hasMore
  accumulatedWidgets: Widget[] // All widgets loaded so far (SSR + paginated)
}

interface CollectionCacheItem {
  collectionPage: Collection | IProductFeedResponseForCollection
  page: number
  hasPaginationTerminated: boolean
  scrollY: number
  /**
   * Widget pagination states keyed by widgetId
   * Used to restore widget pagination state when returning to the page
   */
  widgetPaginationStates?: Record<string, WidgetPaginationState>
  /**
   * Layout pagination state
   * Used to restore layout pagination state when returning to the page
   */
  layoutPaginationState?: LayoutPaginationState
}

const COLLECTION_CACHE_EXPIRY = 5 * 60 * 1000 // 5 minutes
const LRU_CACHE_MAX_SIZE = isMobile ? 10 : 20 // 20MB
const LRU_CACHE_MAX_ITEMS = 3 // 3 items

// This is a singleton class that manages the collection cache
// We can create separate classes for different types of pages for PDP, Search, etc.
class CollectionCacheStateManager {
  private readonly lruCache: MemoryBoundedLRUCache
  private state: Record<string, CollectionCacheItem> = {}

  constructor() {
    this.lruCache = new MemoryBoundedLRUCache(LRU_CACHE_MAX_ITEMS, LRU_CACHE_MAX_SIZE)
    this.state = {}
  }

  setCollectionCache = (collectionCache: CollectionCacheItem, slug: string) => {
    this.state[slug] = collectionCache
    this.lruCache.put(slug, {
      collectionCache: collectionCache,
      cacheExpiry: Date.now() + COLLECTION_CACHE_EXPIRY,
    })
  }

  getCollectionCache = (slug: string): CollectionCacheItem | null => {
    const cacheData = this.lruCache.get(slug)
    if (cacheData?.collectionCache && cacheData.cacheExpiry > Date.now()) {
      return cacheData.collectionCache
    }
    if (cacheData?.collectionCache) {
      this.clearCollectionCache(slug)
    }
    return null
  }

  publishCollectionCache = (slug: string, scrollY: number) => {
    this.lruCache.put(slug, {
      collectionCache: { ...this.state[slug], scrollY },
      cacheExpiry: Date.now() + COLLECTION_CACHE_EXPIRY,
    })
  }

  /**
   * Update widget pagination states in the cache
   * @param slug - Cache key (pathname + searchParams)
   * @param widgetPaginationStates - Map of widgetId to pagination state
   */
  setWidgetPaginationStates = (slug: string, widgetPaginationStates: Record<string, WidgetPaginationState>) => {
    const currentCache = this.getCollectionCache(slug)
    if (currentCache) {
      this.lruCache.put(slug, {
        collectionCache: { ...currentCache, widgetPaginationStates },
        cacheExpiry: Date.now() + COLLECTION_CACHE_EXPIRY,
      })
      // Also update in-memory state
      this.state[slug] = { ...currentCache, widgetPaginationStates }
    }
  }

  /**
   * Get widget pagination state for a specific widget
   * @param slug - Cache key (pathname + searchParams)
   * @param widgetId - Widget identifier
   * @returns Widget pagination state or null if not found
   */
  getWidgetPaginationState = (slug: string, widgetId: string): WidgetPaginationState | null => {
    const cache = this.getCollectionCache(slug)
    if (cache?.widgetPaginationStates?.[widgetId]) {
      return cache.widgetPaginationStates[widgetId]
    }
    return null
  }

  /**
   * Set layout pagination state in the cache
   * @param slug - Cache key (pathname + searchParams)
   * @param layoutPaginationState - Layout pagination state
   */
  setLayoutPaginationState = (slug: string, layoutPaginationState: LayoutPaginationState) => {
    const currentCache = this.getCollectionCache(slug)
    if (currentCache) {
      this.lruCache.put(slug, {
        collectionCache: { ...currentCache, layoutPaginationState },
        cacheExpiry: Date.now() + COLLECTION_CACHE_EXPIRY,
      })
      // Also update in-memory state
      this.state[slug] = { ...currentCache, layoutPaginationState }
    }
  }

  /**
   * Get layout pagination state from cache
   * @param slug - Cache key (pathname + searchParams)
   * @returns Layout pagination state or null if not found
   */
  getLayoutPaginationState = (slug: string): LayoutPaginationState | null => {
    const cache = this.getCollectionCache(slug)
    if (cache?.layoutPaginationState) {
      return cache.layoutPaginationState
    }
    return null
  }

  /**
   * Update scroll position and widget pagination states together
   * Used when navigating away from the page
   * @param slug - Cache key (pathname + searchParams)
   * @param scrollY - Scroll position
   * @param widgetPaginationStates - Map of widgetId to pagination state
   * @param layoutPaginationState - Layout pagination state (optional)
   */
  publishCollectionCacheWithWidgetStates = (
    slug: string,
    scrollY: number,
    widgetPaginationStates?: Record<string, WidgetPaginationState>,
    layoutPaginationState?: LayoutPaginationState,
  ) => {
    const currentCache = this.getCollectionCache(slug) ?? this.state[slug]
    if (!currentCache) {
      // If no cache exists, create a minimal one with required fields
      const newCache: CollectionCacheItem = {
        collectionPage: {} as Collection | IProductFeedResponseForCollection, // Placeholder - will be set by setCollectionCache
        page: 0,
        hasPaginationTerminated: false,
        scrollY,
        ...(widgetPaginationStates && { widgetPaginationStates }),
        ...(layoutPaginationState && { layoutPaginationState }),
      }
      this.lruCache.put(slug, {
        collectionCache: newCache,
        cacheExpiry: Date.now() + COLLECTION_CACHE_EXPIRY,
      })
      this.state[slug] = newCache
      return
    }
    const updatedCache: CollectionCacheItem = {
      ...currentCache,
      scrollY,
      ...(widgetPaginationStates && { widgetPaginationStates }),
      ...(layoutPaginationState && { layoutPaginationState }),
    }
    this.lruCache.put(slug, {
      collectionCache: updatedCache,
      cacheExpiry: Date.now() + COLLECTION_CACHE_EXPIRY,
    })
    this.state[slug] = updatedCache
  }

  clearCollectionCache = (slug: string) => {
    this.lruCache.delete(slug)
  }
  deleteEntriesThatStartsWith = (slug: string) => {
    const cache = (this.lruCache as any).cache as Map<string, any>
    for (const key of cache.keys()) {
      if (key.startsWith(slug)) {
        this.lruCache.delete(key)
      }
    }
  }
}

const collectionCacheStateManager = new CollectionCacheStateManager()

export default collectionCacheStateManager
